import {
    ContractStandardField,
    EntityTemplateField,
    EntityTemplateStep,
    getStandardFieldTranslationKey,
    isContractStandardField,
} from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel, CounterPartyViewModel } from "./Contract.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { hasFieldData } from "../../entityTemplates/fieldValue.ts";
import { InputFieldTypeEnum } from "../../entityTemplates/inputFields/InputFields.types.ts";
import { useTagsMultiSelectAutoUpdater } from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.hooks.ts";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { exhaustiveGuard } from "../../common/utilities.ts";

type ValidatableValue = string | undefined | Date | number | Array<string> | boolean | Array<CounterPartyViewModel>;

type TranslateString = (key: string) => string;

export function isEndDateBeforeStart(startDate: Date | undefined, endDate: Date | undefined) {
    if (startDate && endDate) {
        if (new Date(startDate) >= new Date(endDate as Date)) {
            return true;
        }
    }
    return false;
}

export function useValidateContractStep(contractId: string) {
    const { permissions } = useUserContext();
    const { selectedTags } = useTagsMultiSelectAutoUpdater(permissions.canAccessTags ? contractId : "", TagRelationType.Contract, false);
    const { translateString } = useTranslation();

    function validateStep(step: EntityTemplateStep, contract: ContractModel) {
        const counterPartiesField = step.fields.find((field) => field.standardFieldIdentifier === ContractStandardField.CounterParties);
        const counterPartyErrors = counterPartiesField ? validateCounterParties(contract.counterParties, counterPartiesField) : [];

        return [...validateContractFields(step.fields, contract, selectedTags, permissions.canAccessTags, translateString), ...counterPartyErrors];
    }

    function validateCounterParties(counterParties: Array<CounterPartyViewModel>, field: EntityTemplateField): Array<ValidationError> {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        if (field.required && counterParties.length === 0) {
            const fieldName = getStandardFieldTranslationKey(field.standardFieldIdentifier!);
            errors.push(new ValidationError(fieldName, requiredTranslation));
            return errors;
        }

        counterParties.forEach((counterParty) => {
            if (counterParty.legalEntityId === undefined || counterParty.legalEntityId === null) {
                errors.push(new ValidationError(`${counterParty.id}.legalEntityId`, requiredTranslation));
            }
        });

        return errors;
    }

    return {
        validateStep,
    };
}

/**
 * The field rules shared by the edit page and the AI-assisted update review. A plain function rather than
 * part of the hook: the hook reads the saved contract's tags for itself, which a review holding its own
 * unsaved tag ids cannot use. Counterparties are left to the caller, because the two hold them differently.
 */
export function validateContractFields(
    fields: Array<EntityTemplateField>,
    contract: ContractModel,
    tagIds: Array<string>,
    canAccessTags: boolean,
    translateString: TranslateString
): Array<ValidationError> {
    return fields.flatMap((field) => {
        if (field.standardFieldIdentifier === ContractStandardField.Tags && !canAccessTags) {
            return [];
        }

        if (field.standardFieldIdentifier === ContractStandardField.CounterParties) {
            return [];
        }

        const value = getFieldValue(field, contract, tagIds);
        const error = validateValue(value, field, contract, translateString);
        return error ? [error] : [];
    });
}

function validateValue(value: ValidatableValue, field: EntityTemplateField, contract: ContractModel, translateString: TranslateString): ValidationError | null {
    let fieldName = field.standardFieldIdentifier ? getStandardFieldTranslationKey(field.standardFieldIdentifier!) : field.id!;

    const requiredTranslation = translateString("requiredField");
    const hasValue = hasFieldData(value);
    if (field.required && !hasValue) {
        return new ValidationError(fieldName, requiredTranslation);
    }

    return fieldCustomValidation(value, fieldName, contract, field, translateString);
}

function fieldCustomValidation(
    value: ValidatableValue,
    fieldName: string,
    contract: ContractModel,
    field: EntityTemplateField,
    translateString: TranslateString
) {
    if (field.standardFieldIdentifier === ContractStandardField.EndDate) {
        if (isEndDateBeforeStart(contract.startDate, value as Date)) {
            return new ValidationError(fieldName, translateString("contractInvalidDateRange"));
        }
    }

    if (field.customInputFieldType === InputFieldTypeEnum.AmountField) {
        const amount = (value as any)?.Amount ?? (value as any)?.amount;

        if (isNullOrWhitespace(amount?.toString()) && field.required) {
            return new ValidationError(fieldName, translateString("requiredField"));
        }
    }

    return null;
}

export function getFieldValue(field: EntityTemplateField, model: ContractModel | undefined, selectedTags: Array<string>) {
    if (field.standardFieldIdentifier && isContractStandardField(field.standardFieldIdentifier)) {
        const standardField = field.standardFieldIdentifier;
        switch (standardField) {
            case ContractStandardField.Name:
                return model?.name;
            case ContractStandardField.Description:
                return model?.description;
            case ContractStandardField.ReferenceNumber:
                return model?.referenceNumber;
            case ContractStandardField.Note:
                return model?.note;
            case ContractStandardField.ContractType:
                return model?.contractTypeId;
            case ContractStandardField.Status:
                return model?.status;
            case ContractStandardField.StartDate:
                return model?.startDate;
            case ContractStandardField.EndDate:
                return model?.endDate;
            case ContractStandardField.SignatureDate:
                return model?.signatureDate;
            case ContractStandardField.NotificationOfContractExpiry:
                return model?.contractExpiryNotificationInDays;
            case ContractStandardField.Currency:
                return model?.currencyCode;
            case ContractStandardField.TotalAmount:
                return model?.totalAmount;
            case ContractStandardField.FinalisedAmount:
                return model?.finalizedAmount;
            case ContractStandardField.PaymentTerm:
                return model?.paymentTermId;
            case ContractStandardField.BusinessAreas:
                return model?.businessAreas;
            case ContractStandardField.BusinessAreaAccess:
                return model?.contractAccessBusinessAreas;
            case ContractStandardField.LegalEntities:
                return model?.groupEntities;
            case ContractStandardField.Tags:
                return selectedTags;
            case ContractStandardField.ContractManager:
                return model?.contractManagerId;
            case ContractStandardField.AdditionalUserAccess:
                return model?.additionalAccessUsers;
            case ContractStandardField.ExcludedUsers:
                return model?.excludedUsers;
            case ContractStandardField.VisibleToAllUsers:
                return model?.visibleToAllUsers;
            case ContractStandardField.CounterParties:
                return model?.counterParties;
            case ContractStandardField.SpecialTerms:
                return model?.specialTerms;
            case ContractStandardField.Documents:
                return model?.hasDocuments;
            default:
                return exhaustiveGuard(standardField);
        }
    }

    return model?.customFieldValues.find((x) => x.customInputFieldId == field.customInputFieldId)?.value;
}
